import React, { useState, useRef, useEffect } from 'react'
import {
  TooltipContainer,
  TooltipContent,
  Column,
  Label,
  Value,
  Header,
} from './style'

interface FreightItem {
  id: number
  company: {
    companyName: string
  }
  address: {
    street: string
    number: string
    cep: string
    complement?: string
    neighborhood: {
      name: string
      city: {
        name: string
        state: {
          uf: string
          name: string
        }
      }
    }
  }
  routeId?: number | null
  observation?: string
  type?: string
  deadLine?: string
  orderNumber?: string
  load: {
    height?: number
    width?: number
    length?: number
    cubage?: number
    weight?: number
    detail?: string
  }
}

interface RouterTooltipProps {
  freight: any
  children: React.ReactNode
}

const RouterTooltip: React.FC<RouterTooltipProps> = ({ freight, children }) => {
  const [visible, setVisible] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const tooltipRef = useRef<HTMLDivElement>(null)

  const dataFormatter = (isoDateString: string | undefined): string => {
    if (!isoDateString) {
      return 'N/A'
    }
    const dateUtc = new Date(isoDateString)
    if (isNaN(dateUtc.getTime())) {
      return 'N/A'
    }
    const year = dateUtc.getUTCFullYear()
    const month = dateUtc.getUTCMonth() + 1
    const day = dateUtc.getUTCDate()
    const formattedMonth = month < 10 ? `0${month}` : month
    const formattedDay = day < 10 ? `0${day}` : day
    return `${formattedDay}/${formattedMonth}/${year}`
  }

  const handleMouseEnter = (e: React.MouseEvent) => {
    setPosition({ x: e.clientX, y: e.clientY })
    setVisible(true)
  }

  const handleMouseLeave = () => {
    setVisible(false)
  }

  // Recalcula a posição sempre que a visibilidade mudar
  useEffect(() => {
    if (visible && tooltipRef.current) {
      const windowHeight = window.innerHeight
      const tooltipHeight = tooltipRef.current.offsetHeight
      const newY = position.y + 20

      if (newY + tooltipHeight > windowHeight) {
        const adjustedY = position.y - tooltipHeight - 20
        setPosition((prev) => ({ ...prev, y: adjustedY < 0 ? 10 : adjustedY }))
      }
    }
  }, [visible, position.y])

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {children}
      {visible && (
        <TooltipContainer
          ref={tooltipRef}
          style={{ left: position.x + 20, top: position.y }}
        >
          <Header>Frete - {freight.id}</Header>
          <TooltipContent>
            {/* Primeira Coluna */}
            <Column>
              <Label>Cliente/fornecedor:</Label>
              <Value>{freight.company.companyName}</Value>
              <Label>Tipo:</Label>
              <Value>{freight.type || 'N/A'}</Value>
              <Label>Data limite:</Label>
              <Value>{dataFormatter(freight.deadLine)}</Value>
              <Label>Número da venda:</Label>
              <Value>{freight.orderNumber || 'N/A'}</Value>
            </Column>
            {/* Segunda Coluna */}
            <Column>
              <Label>CEP:</Label>
              <Value>
                {freight.address.cep
                  ? `${String(freight.address.cep).slice(0, 5)}-${String(
                      freight.address.cep,
                    ).slice(5)}`
                  : 'N/A'}
              </Value>
              <Label>Endereço:</Label>
              <Value>{`${freight.address.street}, ${freight.address.number}`}</Value>
              <Label>Bairro:</Label>
              <Value>{freight.address.neighborhood.name}</Value>
              <Label>Cidade/Estado:</Label>
              <Value>{`${freight.address.neighborhood.city.name}/${freight.address.neighborhood.city.state.uf}`}</Value>
              <Label>Complemento:</Label>
              <Value>{freight.address.complement || 'N/A'}</Value>
            </Column>
            {/* Terceira Coluna (Dados de Carga) */}
            <Column>
              <Label>Altura:</Label>
              <Value>{freight.load?.height || 0}</Value>
              <Label>Largura:</Label>
              <Value>{freight.load?.width || 0}</Value>
              <Label>Comprimento:</Label>
              <Value>{freight.load?.length || 0}</Value>
              <Label>Cubagem:</Label>
              <Value>{freight.load?.cubage || 0}</Value>
              <Label>Peso:</Label>
              <Value>{freight.load?.weight || 0}</Value>
              <Label>Detalhamento:</Label>
              <Value>{freight.load?.detail || 'N/A'}</Value>
              <Label>Observações:</Label>
              <Value>{freight.observation || 'N/A'}</Value>
            </Column>
          </TooltipContent>
        </TooltipContainer>
      )}
    </div>
  )
}

export default RouterTooltip
